import { useGetIdentity } from "ra-core";
import { Link, useLocation, useNavigate } from "react-router";
import { useCallback, useMemo } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";
import { Badge } from "@/components/ui/badge";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  filterAccessibleNavItems,
  isAccountsNavActive,
  matchesNavPattern,
} from "@/app/lbsNavAccess";
import {
  filterLbsNavGroups,
  LBS_ACCOUNTS_NAV_ITEM,
  LBS_CONTRACTS_NAV_ITEM,
  LBS_NAV_AFTER_CLIENTS,
  LBS_NAV_GROUPS,
  LBS_NAV_STANDALONE_WITH_ACCOUNTS_HUB,
  splitLbsNavGroups,
  type LbsNavItem,
} from "@/app/navigation";
import { SidebarNavIcon } from "@/app/SidebarNavIcon";
import { canAccessAccountsHub } from "@/modules/accounts/accountsHubAccess";
import { formatUnreadBadgeCount } from "@/modules/messages/messagesUnreadUtils";

import {
  sidebarNavLinkClass,
  sidebarNavLinkCollapsedClass,
} from "@/app/lbsSidebarNavStyles";

// Stable refs to avoid creating new objects on every render, which makes
// Radix Slot (used by TooltipTrigger asChild) treat the Link as changed props
// and re-run composeRefs in a loop. See React.Slot internals.
const LINK_STATE = { _scrollToTop: true } as const;

type LbsSidebarNavProps = {
  websiteMonitorEnabled: boolean;
  messagesUnreadCount: number;
  ticketsNotificationUnread: number;
  mailUnreadCount?: number;
};

export const LbsSidebarNav = ({
  websiteMonitorEnabled,
  messagesUnreadCount,
  ticketsNotificationUnread,
  mailUnreadCount = 0,
}: LbsSidebarNavProps) => {
  const location = useLocation();
  const { data: identity } = useGetIdentity();
  const { state: sidebarState } = useSidebar();

  const isActive = useCallback(
    (pattern: string) => matchesNavPattern(pattern, location.pathname),
    [location.pathname],
  );

  const standaloneItems = useMemo(
    () =>
      filterAccessibleNavItems(identity, LBS_NAV_STANDALONE_WITH_ACCOUNTS_HUB),
    [identity],
  );

  const afterClientsItems = useMemo(
    () => filterAccessibleNavItems(identity, LBS_NAV_AFTER_CLIENTS),
    [identity],
  );

  const contractsNavItem = useMemo(() => {
    const items = filterAccessibleNavItems(identity, [LBS_CONTRACTS_NAV_ITEM]);
    return items[0] ?? null;
  }, [identity]);

  const hubNavItem = useMemo(() => {
    if (!identity) return null;
    return canAccessAccountsHub(identity) ? LBS_ACCOUNTS_NAV_ITEM : null;
  }, [identity]);

  const navGroups = useMemo(() => {
    const groups = filterLbsNavGroups(LBS_NAV_GROUPS, {
      websiteMonitorEnabled,
    });
    return groups
      .map((group) => ({
        ...group,
        items: filterAccessibleNavItems(identity, group.items),
      }))
      .filter((group) => group.items.length > 0);
  }, [identity, websiteMonitorEnabled]);

  // "More" tools (Web Monitor, Hostinger, Reports) live in Settings → Tools.
  const { primary: primaryNavGroups } = useMemo(
    () => splitLbsNavGroups(navGroups),
    [navGroups],
  );

  const hasPrimaryNav =
    standaloneItems.length > 0 ||
    hubNavItem != null ||
    afterClientsItems.length > 0 ||
    primaryNavGroups.length > 0;

  return (
    <SidebarGroup className="gap-0 p-2">
      {standaloneItems.length > 0 || hubNavItem ? (
        <SidebarMenu className="group-data-[collapsible=icon]:gap-1.5">
          {standaloneItems.map((item) => (
            <SidebarNavLink
              key={item.to}
              item={item}
              active={isActive(item.activePattern)}
              collapsed={sidebarState === "collapsed"}
            />
          ))}
          {hubNavItem ? (
            <SidebarNavLink
              key={hubNavItem.to}
              item={hubNavItem}
              active={isAccountsNavActive(location.pathname)}
              collapsed={sidebarState === "collapsed"}
            />
          ) : null}
        </SidebarMenu>
      ) : null}

      {afterClientsItems.length > 0 ? (
        <SidebarMenu className="group-data-[collapsible=icon]:gap-1.5">
          {afterClientsItems.map((item) => (
            <SidebarNavLink
              key={item.to}
              item={item}
              active={isActive(item.activePattern)}
              badgeCount={
                item.to === "/messages"
                  ? messagesUnreadCount
                  : item.to === "/tickets"
                    ? ticketsNotificationUnread
                    : item.to === "/mail"
                      ? mailUnreadCount
                      : 0
              }
              collapsed={sidebarState === "collapsed"}
            />
          ))}
        </SidebarMenu>
      ) : null}

      {primaryNavGroups.map((group, index) => {
        const hasItemsAbove =
          standaloneItems.length > 0 ||
          hubNavItem != null ||
          afterClientsItems.length > 0 ||
          index > 0;
        return (
          <div
            key={group.id}
            className={cn(
              hasItemsAbove &&
                "group-data-[collapsible=icon]:mt-2 group-data-[collapsible=icon]:border-t group-data-[collapsible=icon]:border-sidebar-border group-data-[collapsible=icon]:pt-2",
            )}
          >
            <SidebarGroupLabel
              className={cn(
                "h-auto px-0.5 pb-0.5 text-2xs font-semibold uppercase tracking-wider text-muted-foreground",
                "group-data-[collapsible=icon]:pointer-events-none",
                index === 0 && !hasPrimaryNav ? "mt-0" : "mt-2",
              )}
            >
              {group.label}
            </SidebarGroupLabel>
            <SidebarMenu className="group-data-[collapsible=icon]:gap-1.5">
              {group.items.map((item) => (
                <SidebarNavLink
                  key={item.to}
                  item={item}
                  active={isActive(item.activePattern)}
                  collapsed={sidebarState === "collapsed"}
                />
              ))}
            </SidebarMenu>
          </div>
        );
      })}

      {contractsNavItem ? (
        <SidebarMenu className="mt-0.5 group-data-[collapsible=icon]:gap-1.5">
          <SidebarNavLink
            item={contractsNavItem}
            active={isActive(contractsNavItem.activePattern)}
            collapsed={sidebarState === "collapsed"}
          />
        </SidebarMenu>
      ) : null}
    </SidebarGroup>
  );
};

const SidebarNavLink = ({
  item,
  active,
  badgeCount = 0,
  collapsed = false,
}: {
  item: LbsNavItem;
  active: boolean;
  badgeCount?: number;
  collapsed?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  /** Always land on module list root (e.g. /tickets overview, not a ticket show/inbox). */
  const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    if (item.to !== "/tickets") return;
    if (location.pathname === "/tickets" && !location.search) return;
    event.preventDefault();
    navigate("/tickets", { state: LINK_STATE, viewTransition: true });
  };

  if (collapsed) {
    return (
      <SidebarMenuItem>
        <Tooltip delayDuration={0}>
          {/* Span owns the tooltip ref — Slot+Link can fail to show on hover. */}
          <TooltipTrigger asChild>
            <span className="flex w-full">
              <Link
                to={item.to}
                state={LINK_STATE}
                aria-label={item.label}
                className={sidebarNavLinkCollapsedClass(active)}
                onClick={handleClick}
              >
                <SidebarNavIcon icon={item.icon} active={active} />
                {badgeCount > 0 ? (
                  <NavBadge
                    count={badgeCount}
                    className="absolute -top-0.5 -right-0.5"
                  />
                ) : null}
              </Link>
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            align="center"
            sideOffset={8}
            className="z-[100]"
          >
            {item.label}
          </TooltipContent>
        </Tooltip>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <Link
        to={item.to}
        state={LINK_STATE}
        className={sidebarNavLinkClass(active)}
        onClick={handleClick}
      >
        <SidebarNavIcon icon={item.icon} active={active} />
        <span className="truncate">{item.label}</span>
        {badgeCount > 0 ? (
          <NavBadge count={badgeCount} className="ml-auto" />
        ) : null}
      </Link>
    </SidebarMenuItem>
  );
};

const NavBadge = ({
  count,
  className,
}: {
  count: number;
  className?: string;
}) => (
  <Badge
    variant="default"
    className={cn("rounded-sm border-0 px-1.5 py-0 text-2xs", className)}
  >
    {formatUnreadBadgeCount(count)}
  </Badge>
);
